// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canCheckoutWithCart,
    getCurrentCartState,
    hasOnlyQuoteRequiredProducts,
    isCartCheckoutDisabled,
    isCartEmpty,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import submitRequisition from "@insite/client-framework/Store/Pages/Cart/Handlers/SubmitRequisition";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import LoadingOverlay from "@insite/mobius/LoadingOverlay/LoadingOverlay";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FancyScroll, { FancyScrollRefProps } from "@sc/App/Components/Common/FancyScroll";
import { getCartLineErrorMessages } from "@sc/App/Components/MiniCart/MiniCart.helper";
import MiniCartLine from "@sc/App/Components/MiniCart/MiniCartLine";
import Notify from "@sc/App/Components/Notify";
import { hidePrice } from "@sc/App/Store/Selectors/GeneralSelectors";
import { parseBoolean } from "@sc/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    isPopupShown?: boolean;
    onClose: () => void;
}

const mapStateToProps = (state: ApplicationState) => {
    const cartState = getCurrentCartState(state);
    const cart = cartState.value;
    const isCartLoading = cartState.isLoading;
    const { isUpdatingCart } = state.context;
    const {
        cartSettings: { onePageCheckout },
    } = getSettingsCollection(state);
    return {
        isCheckoutDisabled: isCartCheckoutDisabled(cart),
        canCheckoutWithCart: canCheckoutWithCart(cart),
        isPunchOutOrder: isPunchOutOrder(cart),
        totalCountDisplay: cart?.totalCountDisplay || 0,
        orderSubTotalDisplay: cart?.orderSubTotalDisplay,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        requisitionConfirmationPageUrl: getPageLinkByPageType(state, "RequisitionConfirmationPage")?.url,
        checkoutPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        cart,
        isUpdatingCartLine: state.pages.cart.isUpdatingCartLine,
        isUpdatingCart,
        isLoading: isCartLoading,
        isCartEmpty: isCartEmpty(cart),
        hasOnlyQuoteRequiredProducts: hasOnlyQuoteRequiredProducts(cart),
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        checkoutReviewAndSubmitPageUrl: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url,
        onePageCheckoutPageUrl: getPageLinkByPageType(state, "OnePageCheckoutPage")?.url,
        onePageCheckout,
        isLoadingCart: state.data.carts.isLoading,
        hidePrices: hidePrice(state),
    };
};

const mapDispatchToProps = {
    doPunchOutCheckout,
    updateCartLine,
    removeCartLine,
    preloadCheckoutShippingData,
    submitRequisition,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface MiniCartStyles {
    miniCartWrapper?: InjectableCss;
    cartLinesWrapper?: InjectableCss;
    productsWrapper?: InjectableCss;
    summarySectionWrapper?: InjectableCss;
    miniCartInfoItem?: SmallHeadingAndTextStyles;
    miniCartInfoSubTotal?: SmallHeadingAndTextStyles;
    infoWrapper?: InjectableCss;
    actionWrapper?: InjectableCss;
    viewCartButton?: ButtonPresentationProps;
    checkoutButton?: ButtonPresentationProps;
    centerWrapper?: InjectableCss;
    notify?: TypographyProps;
    emptyCartWrapper?: InjectableCss;
    fancyScrollStyle?: InjectableCss;
}

export const miniCartStyles: MiniCartStyles = {};

const styles = miniCartStyles;

const MiniCart: FC<Props> = ({
    cart,
    canCheckoutWithCart,
    cartPageUrl,
    doPunchOutCheckout,
    isCheckoutDisabled,
    isLoading,
    isPunchOutOrder,
    isUpdatingCart,
    isUpdatingCartLine,
    orderSubTotalDisplay,
    preloadCheckoutShippingData,
    isPopupShown,
    removeCartLine,
    updateCartLine,
    isCartEmpty,
    onePageCheckoutPageUrl,
    checkoutShippingPageUrl,
    checkoutReviewAndSubmitPageUrl,
    onePageCheckout,
    isLoadingCart,
    onClose,
    hidePrices,
}) => {
    const history = useHistory();
    const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
    const parentBoxRef = React.createRef<HTMLDivElement>();
    const fancyScrollRef = React.createRef<FancyScrollRefProps>();

    useEffect(() => {
        if (isPopupShown && fancyScrollRef.current) {
            fancyScrollRef.current.updateScrollable();
        }
    }, [isPopupShown]);

    const hideTippy = () => {
        onClose();
    };

    const qtyOrderedChangeHandler = (cartLine: CartLineModel, qtyOrdered: number) => {
        if (qtyOrdered <= 0) {
            removeCartLine({ cartLineId: cartLine.id });
        } else {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    qtyOrdered,
                },
            });
        }
    };

    const checkoutHandler = () => {
        hideTippy();
        if (!cart) {
            return;
        }

        if (isPunchOutOrder) {
            doPunchOutCheckout();
            return;
        }

        const { isAwaitingApproval, id: cartId, canBypassCheckoutAddress } = cart;
        if (onePageCheckout && onePageCheckoutPageUrl) {
            history.push(isAwaitingApproval ? `${onePageCheckoutPageUrl}?cartId=${cartId}` : onePageCheckoutPageUrl);
            return;
        }

        if (canBypassCheckoutAddress) {
            history.push(checkoutReviewAndSubmitPageUrl!);
            return;
        }

        preloadCheckoutShippingData({
            cartId: isAwaitingApproval ? cartId : undefined,
            onSuccess: () => {
                history.push(
                    isAwaitingApproval ? `${checkoutShippingPageUrl}?cartId=${cartId}` : checkoutShippingPageUrl!,
                );
            },
        });
    };

    const removeCartLineClickHandler = (cartLine: CartLineModel) => {
        removeCartLine({ cartLineId: cartLine.id });
    };

    const handleViewCartClick = () => {
        if (!cartPageUrl) {
            Logger.error(`No Cart Page Url found`);
            return;
        }
        hideTippy();
        history.push(cartPageUrl);
    };

    if (!cart && isLoading) {
        return (
            <StyledWrapper {...styles.centerWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    if (!cart) {
        return null;
    }
    const isCartCheckoutDisabled = isCheckoutDisabled || isCheckoutLoading || !canCheckoutWithCart || isUpdatingCart;
    const cartLines = cart.cartLines ?? [];

    if (!cartLines?.length || isCartEmpty) {
        return (
            <StyledWrapper {...styles.emptyCartWrapper}>
                <Notify
                    {...styles.notify}
                    type="info"
                    data-test-selector="cartlineHeader_warningSomeCartLinesNotPriced"
                >
                    {siteMessage("SC_Cart_Empty")}
                </Notify>
            </StyledWrapper>
        );
    }
    const isQuoteRequired = cartLines.some(cartline => cartline.quoteRequired);
    const productsCannotBePurchased = cartLines.some(o => o.isRestricted || !o.isActive || o.cantBuy);
    const onCreditHold = cart?.properties.onCreditHold;

    return (
        <LoadingOverlay
            id="mini-cart"
            loading={isLoadingCart[cart.id] || isLoading || isUpdatingCart || isUpdatingCartLine}
        >
            <StyledWrapper {...styles.miniCartWrapper}>
                {cartLines.length > 0 && (
                    <>
                        <StyledWrapper {...styles.productsWrapper}>
                            <FancyScroll
                                refElement={parentBoxRef}
                                ref={fancyScrollRef}
                                extendedStyles={styles.fancyScrollStyle}
                            >
                                <StyledWrapper
                                    ref={parentBoxRef}
                                    {...styles.cartLinesWrapper}
                                    data-test-selector="cartLines-container"
                                >
                                    {isQuoteRequired && (
                                        <Notify
                                            {...styles.notify}
                                            type="warning"
                                            data-test-selector="cartline_errorMessage"
                                        >
                                            {siteMessage(
                                                "Custom_SC_Cart_Quote_Required",
                                                "Some items in your cart require a quote.",
                                            )}
                                        </Notify>
                                    )}
                                    {productsCannotBePurchased && (
                                        <Notify
                                            type="warning"
                                            showClose={false}
                                            fullWidth
                                            data-test-selector="cartlineHeader_warningProductsCannotBePurchased"
                                        >
                                            {siteMessage("Cart_ProductsCannotBePurchased")}
                                        </Notify>
                                    )}
                                    {parseBoolean(onCreditHold) && (
                                        <Notify showClose={false} type="warning" fullWidth>
                                            {siteMessage("Custom_SC_Cart_CreditHold")}
                                        </Notify>
                                    )}
                                    {cartLines.map(cartLine => {
                                        const errorMessages = getCartLineErrorMessages(cartLine);
                                        return (
                                            <MiniCartLine
                                                key={`miniCartLine_${cartLine.id}`}
                                                cartLine={cartLine}
                                                cart={cart}
                                                hideTippy={hideTippy}
                                                qtyOrderedChangeHandler={qtyOrderedChangeHandler}
                                                removeCartLineClickHandler={removeCartLineClickHandler}
                                                errorMessages={errorMessages}
                                            />
                                        );
                                    })}
                                </StyledWrapper>
                            </FancyScroll>
                        </StyledWrapper>
                        <StyledWrapper {...styles.summarySectionWrapper}>
                            <StyledWrapper {...styles.infoWrapper}>
                                <SmallHeadingAndText
                                    extendedStyles={styles.miniCartInfoItem}
                                    heading={translate("Items")}
                                    text={cartLines.length}
                                />
                                {!hidePrices && (
                                    <SmallHeadingAndText
                                        extendedStyles={styles.miniCartInfoSubTotal}
                                        heading={translate("Subtotal")}
                                        text={`${orderSubTotalDisplay}`}
                                    />
                                )}
                            </StyledWrapper>
                            <StyledWrapper {...styles.actionWrapper}>
                                <Button
                                    {...styles.viewCartButton}
                                    onClick={handleViewCartClick}
                                    aria-label={translate("View Cart")}
                                >
                                    {translate("View Cart")}
                                </Button>

                                <Button
                                    {...styles.checkoutButton}
                                    onClick={checkoutHandler}
                                    disabled={isCartCheckoutDisabled}
                                    aria-label={`${
                                        cart?.requiresApproval
                                            ? translate("Checkout for Approval")
                                            : translate("Continue to Checkout")
                                    }`}
                                >
                                    {cart?.requiresApproval
                                        ? translate("Checkout for Approval")
                                        : translate("Continue to Checkout")}
                                </Button>
                            </StyledWrapper>
                        </StyledWrapper>
                    </>
                )}
            </StyledWrapper>
        </LoadingOverlay>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(MiniCart);
